import { pointOnPath } from './maps';
import type { MarbleRaceEngine } from './core';
import type { CameraTargetSelection, CameraView, FateMapDefinition } from './types';

interface EventCameraFocus {
  readonly eventId: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export const EVENT_CAMERA_TRIGGER_LEAD_SECONDS = .7;
export const EVENT_CAMERA_PREVIEW_REAL_SECONDS = .85;
const EVENT_CAMERA_LATE_START_GRACE_SECONDS = .35;
const EVENT_CAMERA_RETURN_REAL_SECONDS = .8;
const EVENT_CAMERA_FINISH_PRIORITY_PROGRESS = .82;

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

function selectionWindow<T>(values: readonly T[], centerIndex: number, count: number): readonly T[] {
  const takeCount = Math.min(values.length, count);
  const start = clamp(Math.round(centerIndex - (takeCount - 1) / 2), 0, values.length - takeCount);
  return values.slice(start, start + takeCount);
}

function nextFieldEventCameraFocus(
  engine: MarbleRaceEngine,
  seenEventIds: ReadonlySet<string>,
): EventCameraFocus | null {
  const field = engine.map.field;
  if (!field || engine.map.overviewCamera || engine.phase === 'complete' || engine.finishers.length >= engine.targetRank) return null;
  const event = engine.events.find((candidate) => (
    !seenEventIds.has(candidate.id)
    && engine.elapsed >= candidate.startsAt - EVENT_CAMERA_TRIGGER_LEAD_SECONDS
    && engine.elapsed <= candidate.startsAt + EVENT_CAMERA_LATE_START_GRACE_SECONDS
  ));
  if (!event) return null;
  // 전역 중력 변조는 현재 보고 있는 마블에게도 적용된다. 빈 샤프트로
  // 카메라를 되돌리지 않고 당첨 후보를 계속 보여 준다.
  if (event.type === field.gravityEvent?.type) return null;
  const zone = event.focusZoneIndex !== undefined ? field.eventZones[event.focusZoneIndex]
    : field.eventZones.find((candidate) => candidate.type === event.type);
  if (zone?.rect) {
    return {
      eventId: event.id,
      x: zone.rect.x + zone.rect.w / 2,
      y: zone.rect.y + zone.rect.h / 2,
      width: zone.rect.w,
      height: zone.rect.h,
    };
  }
  if (zone?.circle) {
    return {
      eventId: event.id,
      x: zone.circle.x,
      y: zone.circle.y,
      width: zone.circle.r * 2,
      height: zone.circle.r * 2,
    };
  }
  return null;
}

export function selectCameraTargets(engine: MarbleRaceEngine): CameraTargetSelection {
  if (engine.phase === 'complete') {
    const winner = engine.finishers[engine.targetRank - 1] ?? engine.targetMarble;
    return {
      ids: [winner.id],
      highlightedIds: [winner.id],
      mode: 'winner',
    };
  }

  const unfinished = engine.remaining.sort((left, right) => right.progress - left.progress || left.serial - right.serial);
  if (engine.elapsed < 2) {
    return { ids: unfinished.map((marble) => marble.id), highlightedIds: [], mode: 'start' };
  }

  const slotsUntilResult = engine.targetRank - engine.finishers.length;
  if (slotsUntilResult <= 0) {
    const winner = engine.finishers[engine.targetRank - 1];
    if (winner) {
      return {
        ids: [winner.id],
        highlightedIds: [winner.id],
        mode: 'winner',
      };
    }
  }

  const packCount = engine.marbles.length >= 60 ? 7 : engine.marbles.length >= 24 ? 6 : 5;
  if (engine.winMode === 'last') {
    const boundaryIndex = Math.max(0, unfinished.length - 1);
    const candidates = selectionWindow(unfinished, boundaryIndex, Math.min(4, packCount));
    const boundary = unfinished[boundaryIndex];
    const challenger = unfinished[boundaryIndex - 1];
    return {
      ids: candidates.map((marble) => marble.id),
      highlightedIds: [boundary, challenger].filter(Boolean).map((marble) => marble.id),
      mode: 'last-pack',
    };
  }

  if (engine.winMode === 'rank') {
    const boundaryIndex = clamp(slotsUntilResult - 1, 0, Math.max(0, unfinished.length - 1));
    const candidates = selectionWindow(unfinished, boundaryIndex, packCount);
    const boundary = unfinished[boundaryIndex];
    return {
      ids: candidates.map((marble) => marble.id),
      highlightedIds: boundary ? [boundary.id] : [],
      mode: 'rank-boundary',
    };
  }

  const leaders = unfinished.slice(0, packCount);
  return {
    ids: leaders.map((marble) => marble.id),
    highlightedIds: engine.winMode === 'first' ? leaders.slice(0, 2).map((marble) => marble.id) : [],
    mode: 'leaders',
  };
}

export class FateCameraController {
  readonly view: CameraView = {
    x: 600,
    y: 450,
    zoom: .58,
    targetIds: new Set<string>(),
    highlightedIds: new Set<string>(),
  };
  private realClock = 0;
  private heldUntil = 0;
  private heldSelection: CameraTargetSelection | null = null;
  private inspectTarget: { x: number; y: number } | null = null;
  private inspectZoom: number | null = null;
  private eventFocus: EventCameraFocus | null = null;
  private eventFocusUntil = 0;
  private eventReturnUntil = 0;
  private readonly seenEventFocusIds = new Set<string>();

  get isInspecting(): boolean {
    return this.inspectTarget !== null;
  }

  setInspectTarget(x: number, y: number): void {
    if (!this.inspectTarget) this.inspectZoom = this.view.zoom;
    this.inspectTarget = { x, y };
  }

  clearInspectTarget(): void {
    this.inspectTarget = null;
    this.inspectZoom = null;
  }

  reset(map?: FateMapDefinition): void {
    const start = map ? pointOnPath(map.path, .04) : { x: 600, y: 450 };
    this.view.x = start.x;
    this.view.y = start.y;
    this.view.zoom = .58;
    this.view.targetIds.clear();
    this.view.highlightedIds.clear();
    this.realClock = 0;
    this.heldUntil = 0;
    this.heldSelection = null;
    this.eventFocus = null;
    this.eventFocusUntil = 0;
    this.eventReturnUntil = 0;
    this.seenEventFocusIds.clear();
    this.clearInspectTarget();
  }

  update(
    engine: MarbleRaceEngine,
    viewportWidth: number,
    viewportHeight: number,
    realDeltaSeconds: number,
    reduceMotion: boolean,
  ): CameraTargetSelection {
    const clockDeltaSeconds = Number.isFinite(realDeltaSeconds) ? Math.max(0, realDeltaSeconds) : 0;
    // 저FPS에서도 선택 유지 시간과 카메라 스무딩이 벽시계와 같은 속도로 흐른다.
    // 단, 오래 멈춘 한 프레임이 화면을 완전히 순간이동시키지 않도록 스무딩만 0.5초로 제한한다.
    const smoothingDeltaSeconds = Math.min(.5, clockDeltaSeconds);
    this.realClock += clockDeltaSeconds;
    const candidate = selectCameraTargets(engine);
    const candidateKey = `${candidate.mode}:${candidate.ids.join(',')}`;
    const heldKey = this.heldSelection ? `${this.heldSelection.mode}:${this.heldSelection.ids.join(',')}` : '';
    const urgentModeChange = candidate.mode !== this.heldSelection?.mode
      && (candidate.mode === 'rank-boundary' || candidate.mode === 'last-pack' || candidate.mode === 'winner');
    if (!this.heldSelection || candidateKey === heldKey || this.realClock >= this.heldUntil || urgentModeChange) {
      this.heldSelection = candidate;
      this.heldUntil = this.realClock + .72;
    }
    let selection = this.heldSelection;
    let selected = (selection.mode === 'winner' ? engine.marbles : engine.remaining)
      .filter((marble) => selection.ids.includes(marble.id));
    if (selection.mode !== 'winner' && selected.length < selection.ids.length) {
      selection = candidate;
      this.heldSelection = candidate;
      this.heldUntil = this.realClock + .72;
      selected = engine.remaining.filter((marble) => selection.ids.includes(marble.id));
    }
    if (selected.length === 0) return selection;
    if (engine.map.field?.circuit && selection.mode !== 'start') {
      const anchor = selected.find((marble) => marble.id === selection.highlightedIds[0]) ?? selected[0];
      // 랩 차이 또는 지름길 분기로 멀어진 공 때문에 카메라가 빈 인필드를 보지 않게 한다.
      selected = selected.filter((marble) => marble.lap === anchor.lap && Math.hypot(marble.x - anchor.x, marble.y - anchor.y) <= 420);
    }
    const xs = selected.map((marble) => marble.x);
    const ys = selected.map((marble) => marble.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const focus = selected.find((marble) => marble.id === selection.highlightedIds[0]) ?? selected[0];
    const averageProgress = selected.reduce((sum, marble) => sum + marble.progress, 0) / selected.length;
    // 반복 경로의 누적 %로 좌표를 찾으면 다른 랩/지름길로 카메라가 튄다.
    // 실제 추적 공의 이동 방향만 짧게 예측하며 랩선에서도 연속적으로 따라간다.
    const ahead = engine.map.field?.circuit
      ? { x: focus.x + clamp(focus.vx * .3, -75, 75), y: focus.y + clamp(focus.vy * .3, -75, 75) }
      : pointOnPath(engine.map.path, Math.min(1, averageProgress + .1));
    const packX = (minX + maxX) / 2;
    const packY = (minY + maxY) / 2;
    let desiredX = selection.mode === 'start'
      ? packX * .58 + ahead.x * .42
      : focus.x * .6 + packX * .28 + ahead.x * .12;
    let desiredY = selection.mode === 'start'
      ? packY * .58 + ahead.y * .42
      : focus.y * .6 + packY * .28 + ahead.y * .12;
    const fitZoom = Math.min(
      viewportWidth / Math.max(720, maxX - minX + 420),
      viewportHeight / Math.max(430, maxY - minY + 300),
    );
    const cameraZone = engine.map.cameraZones.find((zone) => averageProgress >= zone.start && averageProgress <= zone.end);
    let zoneZoom = cameraZone?.zoom ?? 1.2;
    if (!reduceMotion && cameraZone?.id === 'collision') {
      const local = clamp((averageProgress - cameraZone.start) / Math.max(.001, cameraZone.end - cameraZone.start), 0, 1);
      zoneZoom *= 1 + local * .025;
    }
    const countZoomCap = engine.marbles.length >= 60
      ? 1.05
      : engine.marbles.length >= 32
        ? 1.16
        : engine.marbles.length >= 16
          ? 1.3
          : 1.48;
    const viewportZoomCap = viewportWidth < 620 ? 1.02 : 1.48;
    const maximumZoom = Math.min(zoneZoom, countZoomCap, viewportZoomCap);
    const minimumCameraZoom = engine.map.minimumCameraZoom ?? .42;
    const minimumRaceZoom = viewportWidth < 620
      ? .42
      : selection.mode === 'start'
        ? .58
        : .76;
    let desiredZoom = clamp(Math.min(fitZoom, maximumZoom), minimumRaceZoom, maximumZoom);
    if (reduceMotion) desiredZoom = clamp(desiredZoom, .46, Math.min(1, maximumZoom));

    // 이벤트는 배속과 무관하게 실제 시간 기준으로 잠깐만 비춘다. 이후에는 진행 중인
    // 이벤트보다 당첨 순위 후보 추적을 우선하고 빠르게 원래 카메라로 복귀한다.
    const trackedMarble = engine.marbles.find((marble) => marble.id === selection.highlightedIds[0]);
    const finalApproach = (trackedMarble?.progress ?? 0) >= EVENT_CAMERA_FINISH_PRIORITY_PROGRESS;
    const targetResolved = engine.phase === 'complete' || engine.finishers.length >= engine.targetRank;
    if (this.eventFocus && (this.realClock >= this.eventFocusUntil || finalApproach || targetResolved)) {
      this.eventFocus = null;
      this.eventReturnUntil = this.realClock + EVENT_CAMERA_RETURN_REAL_SECONDS;
    }
    if (!this.eventFocus && !this.inspectTarget && !finalApproach && !targetResolved) {
      const candidateFocus = nextFieldEventCameraFocus(engine, this.seenEventFocusIds);
      if (candidateFocus) {
        this.eventFocus = candidateFocus;
        this.eventFocusUntil = this.realClock + EVENT_CAMERA_PREVIEW_REAL_SECONDS;
        this.seenEventFocusIds.add(candidateFocus.eventId);
      }
    }
    const eventFocus = this.inspectTarget ? null : this.eventFocus;
    const returningFromEvent = !this.inspectTarget && !eventFocus && this.realClock < this.eventReturnUntil;
    const overview = engine.map.overviewCamera;
    const showingOverview = !!overview && !this.inspectTarget && !eventFocus
      && selection.mode !== 'start' && selection.mode !== 'winner'
      && focus.progress >= overview.start && focus.progress < overview.end;
    if (showingOverview) {
      desiredX = overview.x;
      desiredY = overview.y;
      desiredZoom = clamp(Math.min(
        viewportWidth / (overview.width + 72),
        viewportHeight / (overview.height + 72),
      ), minimumCameraZoom, maximumZoom);
    }
    if (eventFocus) {
      const horizontalPadding = viewportWidth < 620 ? 30 : 54;
      const verticalPadding = viewportWidth < 620 ? 26 : 44;
      const eventFitZoom = Math.min(
        viewportWidth / Math.max(1, eventFocus.width + horizontalPadding * 2),
        viewportHeight / Math.max(1, eventFocus.height + verticalPadding * 2),
      );
      desiredX = eventFocus.x;
      desiredY = eventFocus.y;
      desiredZoom = clamp(Math.min(eventFitZoom, maximumZoom), minimumCameraZoom, maximumZoom);
      if (reduceMotion) desiredZoom = clamp(desiredZoom, engine.map.minimumCameraZoom ?? .46, Math.min(1, maximumZoom));
    }

    const finishCamera = engine.map.finishCamera;
    if (finishCamera && focus.y >= finishCamera.entryY && !eventFocus && selection.mode !== 'start') {
      // 뒤쪽의 멀리 떨어진 마블 때문에 결승봉을 다시 축소하지 않는다.
      // 모바일은 좌우 여백을 줄이고, 아래의 후보 안전 여백 보정으로 공을 화면 안에 둔다.
      const frameWidth = viewportWidth < 620 ? Math.min(260, finishCamera.width) : finishCamera.width;
      desiredX = finishCamera.x;
      desiredY = finishCamera.y;
      desiredZoom = Math.min(viewportWidth / (frameWidth + 40), viewportHeight / (finishCamera.height + 40), 2.2);
      if (reduceMotion) desiredZoom = Math.min(1, desiredZoom);
    }

    if (this.inspectTarget) {
      desiredX = this.inspectTarget.x;
      desiredY = this.inspectTarget.y;
      desiredZoom = clamp(
        overview ? Math.max(.76, this.inspectZoom ?? this.view.zoom) : this.inspectZoom ?? this.view.zoom,
        .42,
        viewportWidth < 620 ? 1.02 : 1.48,
      );
    }

    const halfViewWidth = viewportWidth / Math.max(minimumCameraZoom, desiredZoom) / 2;
    const halfViewHeight = viewportHeight / Math.max(minimumCameraZoom, desiredZoom) / 2;
    let boundedDesiredX = halfViewWidth * 2 >= engine.map.worldWidth
      ? engine.map.worldWidth / 2
      : clamp(desiredX, halfViewWidth, engine.map.worldWidth - halfViewWidth);
    let boundedDesiredY = halfViewHeight * 2 >= engine.map.worldHeight
      ? engine.map.worldHeight / 2
      : clamp(desiredY, halfViewHeight, engine.map.worldHeight - halfViewHeight);

    if (!this.inspectTarget && !eventFocus && !showingOverview && selection.highlightedIds.length > 0) {
      const safeMargin = viewportWidth < 620 ? 22 : 30;
      const focusHalfWidth = Math.max(0, (viewportWidth / 2 - safeMargin) / desiredZoom);
      const focusHalfHeight = Math.max(0, (viewportHeight / 2 - safeMargin) / desiredZoom);
      boundedDesiredX = clamp(boundedDesiredX, focus.x - focusHalfWidth, focus.x + focusHalfWidth);
      boundedDesiredY = clamp(boundedDesiredY, focus.y - focusHalfHeight, focus.y + focusHalfHeight);
    }

    const positionRate = this.inspectTarget
      ? reduceMotion ? 20 : 11
      : eventFocus
        ? reduceMotion ? 18 : 16
        : returningFromEvent
          ? reduceMotion ? 4 : 10
          : reduceMotion ? 2.2 : 3.6;
    const zoomRate = this.inspectTarget
      ? 10
      : eventFocus
        ? 14
        : returningFromEvent
          ? reduceMotion ? 3.2 : 8
          : reduceMotion ? 2 : 2.8;
    const positionSmoothing = 1 - Math.exp(-smoothingDeltaSeconds * positionRate);
    const zoomSmoothing = 1 - Math.exp(-smoothingDeltaSeconds * zoomRate);
    this.view.x += (boundedDesiredX - this.view.x) * positionSmoothing;
    this.view.y += (boundedDesiredY - this.view.y) * positionSmoothing;
    this.view.zoom += (desiredZoom - this.view.zoom) * zoomSmoothing;
    this.view.targetIds.clear();
    selection.ids.forEach((id) => this.view.targetIds.add(id));
    this.view.highlightedIds.clear();
    selection.highlightedIds.forEach((id) => this.view.highlightedIds.add(id));
    return selection;
  }
}
